import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, OnInit, OnChanges, EventEmitter, Output, Input } from '@angular/core';
import { ListItemEditorComponent } from './../../workflow/data-entities/list-data-entity/list-data-entity-editor/list-item-editor/list-item-editor.component';
import { ListOption } from '../../../models/listOption';
import { CustomListAttribute, CustomListItemAttributeValue } from '../../../models/custom-list';
import { Utilities } from '../../../services/utilities';

@Component({
    selector: 'wm-list-editor',
    templateUrl: './list-editor.component.html',
    styleUrls: ['./list-editor.component.css'],
    standalone: false
})
export class ListEditorComponent implements OnInit, OnChanges {
  @Output() public listChange: EventEmitter<ListOption[]> = new EventEmitter<
    ListOption[]
  >();
  @Output() public delete: EventEmitter<ListOption> = new EventEmitter<
    ListOption
  >();
  @Output() public add: EventEmitter<ListOption> = new EventEmitter<
    ListOption
  >();

  @Input() public listItems: ListOption[] = [];
  @Input() public attributes: CustomListAttribute[] = [];
  @Input() public showCustomListAttributes = false;
  @Input() public canEdit = false;
  @Input() isRoleMembersList = false;
  @Input() showImageEditor = true;

  public currentItem: string = null;

  public showNew = false;
  public newItem: ListOption = new ListOption({ text: '', value: '' });
  public bulkAddText = '';

  constructor(private modalService: NgbModal) {}

  ngOnInit() {}

  ngOnChanges() {
    // Initialize attributeValues for existing items when attributes change
    if (this.hasCustomListAttributes && this.listItems && this.listItems.length > 0) {
      this.listItems.forEach(item => {
        this.initializeAttributeValues(item);
      });
    }
  }

  private initializeAttributeValues(item: ListOption) {
    const itemAny = item as any;
    if (!itemAny.attributeValues) {
      itemAny.attributeValues = [];
    }

    // Ensure all attributes have values (even if empty)
    if (this.hasCustomListAttributes) {
      for (const attr of this.attributes) {
        const existingValue = itemAny.attributeValues.find(av => av.customListAttributeId === attr.id);
        if (!existingValue) {
          itemAny.attributeValues.push({
            id: Utilities.generateId(),
            customListItemId: item.id,
            customListAttributeId: attr.id,
            value: ''
          } as CustomListItemAttributeValue);
        }
      }
    }
  }

  public emitChange() {
    for (let index = 0; index < this.listItems.length; index++) {
      const element = this.listItems[index];
      element.position = index;
    }

    this.listChange.emit(this.listItems);
  }

  public removeItem(t) {
    const i = this.listItems.indexOf(t);
    this.listItems.splice(i, 1);
    this.emitChange();
    this.delete.emit(t);
  }

  public addItem(item: ListOption, editor: ListItemEditorComponent) {
    // Initialize attribute values for new item
    if (this.hasCustomListAttributes) {
      this.initializeAttributeValues(item);
    }

    this.listItems.push(item);
    this.showNew = false;
    this.newItem = new ListOption({ text: '', value: '' });
    this.emitChange();
    editor.reset();
    this.add.emit(this.newItem);
  }

  public getAttributeValue(item: ListOption, attributeId: string): string {
    if (!this.showCustomListAttributes) {
      return '';
    }

    const itemAny = item as any;
    if (!itemAny.attributeValues) {
      return '';
    }
    const attrValue = itemAny.attributeValues.find(av => av.customListAttributeId === attributeId);
    return attrValue ? attrValue.value : '';
  }

  public setAttributeValue(item: ListOption, attributeId: string, value: string) {
    if (!this.showCustomListAttributes) {
      return;
    }

    const itemAny = item as any;
    if (!itemAny.attributeValues) {
      itemAny.attributeValues = [];
    }

    const attrValue = itemAny.attributeValues.find(av => av.customListAttributeId === attributeId);
    if (attrValue) {
      attrValue.value = value;
    } else {
      itemAny.attributeValues.push({
        id: Utilities.generateId(),
        customListItemId: item.id,
        customListAttributeId: attributeId,
        value: value
      } as CustomListItemAttributeValue);
    }

    this.emitChange();
  }

  public getCurrentItem(): ListOption | undefined {
    return this.listItems.find(i => i.id === this.currentItem);
  }

  public getNewItemAttributeValue(attributeId: string): string {
    return this.getAttributeValue(this.newItem, attributeId);
  }

  public setNewItemAttributeValue(attributeId: string, value: string) {
    this.setAttributeValue(this.newItem, attributeId, value);
  }

  public addNewItem() {
    // Initialize attribute values for new item
    if (this.hasCustomListAttributes) {
      this.initializeAttributeValues(this.newItem);
    }

    this.listItems.push(this.newItem);
    this.showNew = false;
    this.newItem = new ListOption({ text: '', value: '' });
    this.emitChange();
    this.add.emit(this.newItem);
  }

  public cancelNewItem() {
    this.showNew = false;
    this.newItem = new ListOption({ text: '', value: '' });
  }

  public get hasCustomListAttributes(): boolean {
    return this.showCustomListAttributes && this.attributes && this.attributes.length > 0;
  }

  public saveItem() {
    this.currentItem = null;
    this.emitChange();
  }

  public open(content) {
    this.modalService
      .open(content, { ariaLabelledBy: 'modal-bulk-title' })
      .result.then(
        () => {
          if (this.bulkAddText) {
            const list = this.bulkAddText.split(',');
            const filteredList = list.filter(item => !!item.trim());
            const listOptions = filteredList.map(item => {
              const text = item.trim();
              if (!text) {
                return;
              }
              const opt = new ListOption({ text, value: text });

              return opt;
            });

            this.listItems.push(...listOptions);
          }

          this.bulkAddText = '';
          this.emitChange();
        },
        () => {}
      );
  }
}
